import React, { Suspense, useEffect, useRef, useMemo, useState } from 'react'
import { Canvas, useThree, useFrame } from '@react-three/fiber'
import { OrbitControls, useGLTF } from '@react-three/drei'
import * as THREE from 'three'
import { SimpleSquarePoolModel } from '../features/scene-3d/objects/Pool/models/SimpleSquarePoolModel'
import { PROC_POOL_GEOMETRY } from '../features/scene-3d/objects/Pool/config/constants'
import { calculateProjectorPositions } from '../features/scene-3d/objects/equipment/projectors/logic/core'
import { getProjectorScale } from '../features/scene-3d/objects/equipment/projectors/logic/config'
import { calculateLadderPositions } from '../features/scene-3d/objects/equipment/ladders/logic/core'
import { calculateNozzlePositions } from '../features/scene-3d/objects/equipment/nozzles/logic/core'
import { calculateSkimmerPositions } from '../features/scene-3d/objects/equipment/skimmers/logic/core'
import { getSkimmerModelPath } from '../features/scene-3d/objects/equipment/skimmers/logic/positioning'
import { calculateDrainPositions } from '../features/scene-3d/objects/equipment/drains/logic/core'

const { SCALE_XZ, WALL_HEIGHT_FACTOR, POOL_GROUP_Y_OFFSET } = PROC_POOL_GEOMETRY

// Лестница GROUP_1 (NMU/SF типа) — для скиммерного бассейна
const M_LADDER    = '/models/ladders/ladder4.glb'
const M_PROJECTOR = '/models/projectors/projector.glb'
const M_NOZZLE    = '/models/nozzles/nozzle.glb'
const M_DRAIN     = '/models/drains/drain.glb'

// Preloads вызываются из компонента (useEffect) а не на уровне модуля,
// чтобы не блокировать начальную загрузку страницы до первого рендера.

// ─── НАСТРОЙКИ КАМЕРЫ ──────────────────────────────────────────────────────────
const CAM_AZIMUTH_DEG   = 50
const CAM_ELEVATION_DEG = 34
const CAM_DIST_FACTOR   = 2.35
const CAM_DIST_MIN      = 2.5
const CAM_LOOK_Y_FACTOR = 0
const CAM_FOV           = 50

function calcCamera(len, wid, dep) {
  const L = len * SCALE_XZ
  const W = wid * SCALE_XZ
  const H = dep * WALL_HEIGHT_FACTOR
  const radius = Math.sqrt((L / 2) ** 2 + (W / 2) ** 2 + (H * 0.5) ** 2)
  const dist = Math.max(radius * CAM_DIST_FACTOR, CAM_DIST_MIN)
  const az = (CAM_AZIMUTH_DEG * Math.PI) / 180
  const el = (CAM_ELEVATION_DEG * Math.PI) / 180
  const px = Math.cos(az) * dist * Math.cos(el)
  const py = dist * Math.sin(el) + POOL_GROUP_Y_OFFSET + H * 0.5
  const pz = Math.sin(az) * dist * Math.cos(el)
  const lookY = POOL_GROUP_Y_OFFSET + H * CAM_LOOK_Y_FACTOR
  return { pos: [px, py, pz], look: [0, lookY, 0] }
}

function CameraSetup({ len, wid, dep, controlsRef }) {
  const { camera, invalidate } = useThree()
  useEffect(() => {
    const { pos, look } = calcCamera(len, wid, dep)
    camera.position.set(...pos)
    camera.updateProjectionMatrix()
    if (controlsRef.current) {
      controlsRef.current.target.set(...look)
      controlsRef.current.update()
    } else {
      camera.lookAt(...look)
    }
    invalidate()
  }, [len, wid, dep, camera, controlsRef, invalidate])
  return null
}

function Lights({ len, wid, dep }) {
  const W = wid * SCALE_XZ
  const L = len * SCALE_XZ
  const H = dep * WALL_HEIGHT_FACTOR
  return (
    <>
      <ambientLight intensity={0.75} color="#c4dde8" />
      <directionalLight position={[W * 2.5, H * 8 + 4, L * 1.8]} intensity={2.0} color="#ffffff" castShadow={false} />
      <directionalLight position={[-W * 1.8, H * 3 + 1.5, -L * 1.2]} intensity={0.55} color="#5b9ab8" castShadow={false} />
      <pointLight position={[0, H * 0.5 + POOL_GROUP_Y_OFFSET + 0.6, 0]} intensity={0.9} color="#20c8d8" distance={L + W + 5} decay={2} />
    </>
  )
}

// ─── Прожекторы ────────────────────────────────────────────────────────────────
// Количество авто-рассчитывается от периметра: round(perimeter / 4.5), чётное
function PoolProjectors({ len, wid, dep }) {
  const { scene } = useGLTF(M_PROJECTOR)
  const dims = useMemo(() => ({ length: len, width: wid, depth: dep }), [len, wid, dep])
  const instances = useMemo(() => calculateProjectorPositions(dims, null, 'скиммерный'), [dims])
  const scale = useMemo(() => getProjectorScale(dep, len, wid), [dep, len, wid])
  const clones = useMemo(() => {
    if (!scene || !instances.length) return []
    return instances.map(() => scene.clone(true))
  }, [scene, instances])

  if (!clones.length) return null

  return (
    <group>
      {clones.map((clone, i) => {
        const inst = instances[i]
        const p = Array.isArray(inst.position) ? inst.position : [inst.position.x, inst.position.y, inst.position.z]
        const r = Array.isArray(inst.rotation) ? inst.rotation : [inst.rotation?.x ?? 0, inst.rotation?.y ?? 0, inst.rotation?.z ?? 0]
        return (
          <group key={i} position={p} rotation={r} scale={scale}>
            <primitive object={clone} />
          </group>
        )
      })}
      {/* Холодное LED-свечение — смещено вглубь бассейна по нормали стены */}
      {instances.map((inst, i) => {
        const p = Array.isArray(inst.position) ? inst.position : [inst.position.x, inst.position.y, inst.position.z]
        const n = inst.normal || [0, 0, 0]
        const lp = [p[0] + n[0] * 0.45, p[1], p[2] + n[2] * 0.45]
        return (
          <pointLight key={`l${i}`} position={lp} color="#B8F0FF" intensity={0.7} distance={4.5} decay={2} />
        )
      })}
    </group>
  )
}

// ─── Лестница: GROUP_1 (NMU/SF) — скиммерный бассейн ──────────────────────────
function PoolLadder({ len, wid, dep }) {
  const { scene } = useGLTF(M_LADDER)
  const instances = useMemo(() =>
    calculateLadderPositions(
      { length: len, width: wid, depth: dep },
      1,
      'скиммерный',
      { modelPath: M_LADDER }
    ),
    [len, wid, dep]
  )
  const clones = useMemo(() => {
    if (!scene || !instances.length) return []
    return instances.map(() => scene.clone(true))
  }, [scene, instances])

  if (!clones.length) return null

  return (
    <group>
      {clones.map((clone, i) => {
        const inst = instances[i]
        const p = Array.isArray(inst.position) ? inst.position : [inst.position.x, inst.position.y, inst.position.z]
        const r = Array.isArray(inst.rotation) ? inst.rotation : [0, inst.rotation?.y ?? 0, 0]
        const s = inst.scale || [0.8, 0.8, 0.8]
        return (
          <group key={i} position={p} rotation={r} scale={s}>
            <primitive object={clone} />
          </group>
        )
      })}
    </group>
  )
}

// ─── Скиммеры: количество передаётся снаружи (SceneContents) ─────────────────
function PoolSkimmers({ len, wid, dep, count }) {
  const skimmerName = 'EM0010'
  const modelPath   = getSkimmerModelPath(skimmerName)
  const { scene }   = useGLTF(modelPath)
  const instances   = useMemo(() =>
    calculateSkimmerPositions({ length: len, width: wid, depth: dep }, count, 'скиммерный', null, skimmerName),
    [len, wid, dep, count]
  )
  const clones = useMemo(() => {
    if (!scene || !instances.length) return []
    return instances.map(() => scene.clone(true))
  }, [scene, instances])

  if (!clones.length) return null

  return (
    <group>
      {clones.map((clone, i) => {
        const inst = instances[i]
        const p = inst.position || [0, 0, 0]
        const r = inst.rotation || [0, 0, 0]
        const s = inst.scale   || [0.2, 0.2, 0.2]
        return (
          <group key={i} position={p} rotation={r} scale={s}>
            <primitive object={clone} />
          </group>
        )
      })}
    </group>
  )
}

// ─── Форсунки: только на WALL_LEFT — напротив скиммеров (WALL_RIGHT) ──────────
// Количество = skimmerCount * 2. calculateNozzlePositions уже ставит форсунки
// на WALL_LEFT (isLengthMain=true) и слегка утапливает их в стену (wallOffset≈0.013).
// Используем извлечённую геометрию+материал (как основной app) — это устраняет смещения
// от корневых трансформов GLB-сцены, которые делали primitive невидимым.
function PoolNozzlesEmbed({ len, wid, dep, skimmerCount }) {
  const { scene }   = useGLTF(M_NOZZLE)
  const nozzleCount = skimmerCount * 2

  // Извлекаем геометрию и материал напрямую из первого меша сцены
  const { geometry, material } = useMemo(() => {
    if (!scene) return {}
    let found = null
    scene.traverse(child => {
      if (child.isMesh && !child.name?.toLowerCase().includes('text') && !found)
        found = child
    })
    if (!found) return {}
    const mat = Array.isArray(found.material) ? found.material[0].clone() : found.material.clone()
    return { geometry: found.geometry, material: mat }
  }, [scene])

  const instances = useMemo(() =>
    calculateNozzlePositions({ length: len, width: wid, depth: dep }, nozzleCount, 'скиммерный', null),
    [len, wid, dep, nozzleCount]
  )

  if (!geometry || !instances.length) return null

  return (
    <group>
      {instances.map((inst, i) => {
        const [x, y, z] = inst.position || [0, 0, 0]
        const n  = inst.normal   || [0, 0, 0]
        const PUSH = 0
        const p = [x + n[0] * PUSH, y, z + n[2] * PUSH]
        const r = inst.rotation || [0, 0, 0]
        const s = (inst.scale || [0.075, 0.075, 0.075]).map(v => v * 1.5)
        return (
          <mesh key={i} position={p} rotation={r} scale={s}
            geometry={geometry} material={material} />
        )
      })}
    </group>
  )
}

// ─── Донный слив: 1 слив ближе к стене скиммеров ─────────────────────────────
// calculateDrainPositions (isLengthLonger=true) ставит слив у WALL_RIGHT:
// x = innerWidth/2 - wallInset — той же стене, где скиммеры.
function PoolDrain({ len, wid, dep }) {
  const { scene } = useGLTF(M_DRAIN)
  const instances = useMemo(() =>
    calculateDrainPositions({ length: len, width: wid, depth: dep }, 1, 'скиммерный', null),
    [len, wid, dep]
  )
  const clones = useMemo(() => {
    if (!scene || !instances.length) return []
    return instances.map(() => scene.clone(true))
  }, [scene, instances])

  if (!clones.length) return null

  return (
    <group>
      {clones.map((clone, i) => {
        const inst = instances[i]
        const [px, py, pz] = inst.position || [0, 0, 0]
        const r = inst.rotation || [0, 0, 0]
        const s = inst.scale    || [0.15, 0.15, 0.15]
        return (
          <group key={i} position={[px, py - 0.06, pz]} rotation={r} scale={s}>
            <primitive object={clone} />
          </group>
        )
      })}
    </group>
  )
}

// ─── Форс-ренде при переключении оборудования (frameloop="demand") ─────────────
function EqInvalidator({ light, ladder, embed }) {
  const { invalidate } = useThree()
  useEffect(() => { invalidate() }, [light, ladder, embed, invalidate])
  return null
}

// ─── Плавное появление: fade-in при монтировании ──────────────────────────────
function FadeInGroup({ children }) {
  const groupRef = useRef()
  const alphaRef = useRef(0)
  const doneRef  = useRef(false)
  const { invalidate } = useThree()

  useFrame((_, delta) => {
    if (doneRef.current || !groupRef.current) return
    alphaRef.current = Math.min(1, alphaRef.current + delta * 4)
    groupRef.current.traverse(obj => {
      if (!obj.isMesh) return
      if (!obj.userData._fadeReady) {
        obj.userData._origTransparent = obj.material.transparent
        obj.userData._origOpacity     = obj.material.opacity ?? 1
        obj.material = obj.material.clone()
        obj.material.transparent = true
        obj.userData._fadeReady = true
      }
      obj.material.opacity = alphaRef.current * obj.userData._origOpacity
    })
    if (alphaRef.current >= 1) {
      groupRef.current.traverse(obj => {
        if (!obj.isMesh || !obj.userData._fadeReady) return
        obj.material.transparent = obj.userData._origTransparent
        obj.material.opacity     = obj.userData._origOpacity
        obj.userData._fadeReady  = false
      })
      doneRef.current = true
    } else {
      invalidate()
    }
  })

  return <group ref={groupRef}>{children}</group>
}

// ─── Сцена ─────────────────────────────────────────────────────────────────────
function ReadySignal({ onReady }) {
  const firedRef = useRef(false)
  useFrame(() => {
    if (!firedRef.current) { firedRef.current = true; onReady() }
  })
  return null
}

function SceneContents({ len, wid, dep, finObj, eq, autoRotate = false, autoRotateSpeed = 1.5, enableRotate = true, onReady }) {
  const controlsRef  = useRef()
  const skimmerCount = Math.max(2, Math.ceil(len * wid / 30))

  return (
    <>
      <CameraSetup len={len} wid={wid} dep={dep} controlsRef={controlsRef} />
      <Lights len={len} wid={wid} dep={dep} />
      <OrbitControls
        ref={controlsRef}
        enablePan={false}
        enableZoom={false}
        enableRotate={enableRotate}
        enableDamping={true}
        dampingFactor={0.06}
        minPolarAngle={0.15}
        maxPolarAngle={Math.PI / 2.05}
        autoRotate={autoRotate}
        autoRotateSpeed={autoRotateSpeed}
        makeDefault
      />
      {/* Бассейн — процедурный, не требует Suspense */}
      <SimpleSquarePoolModel
        length={len}
        width={wid}
        depth={dep}
        borderType="normal"
        selectedFinishing={finObj || null}
      />
      {/* Каждый тип оборудования — в своём Suspense, чтобы переключение одного
          не затрагивало остальные и не вызывало повторный suspense всего блока */}
      <Suspense fallback={null}>
        {eq?.light && <FadeInGroup key="proj"><PoolProjectors len={len} wid={wid} dep={dep} /></FadeInGroup>}
      </Suspense>
      <Suspense fallback={null}>
        {eq?.ladder && <FadeInGroup key="ladr"><PoolLadder len={len} wid={wid} dep={dep} /></FadeInGroup>}
      </Suspense>
      <Suspense fallback={null}>
        {eq?.embed && (
          <FadeInGroup key="embd">
            <PoolSkimmers     len={len} wid={wid} dep={dep} count={skimmerCount} />
            <PoolNozzlesEmbed len={len} wid={wid} dep={dep} skimmerCount={skimmerCount} />
            <PoolDrain        len={len} wid={wid} dep={dep} />
          </FadeInGroup>
        )}
      </Suspense>
      <EqInvalidator light={!!eq?.light} ladder={!!eq?.ladder} embed={!!eq?.embed} />
      {onReady && <ReadySignal onReady={onReady} />}
    </>
  )
}

// ─── Экспорт ───────────────────────────────────────────────────────────────────
export function LandingPoolPreview({ len, wid, dep, finObj, eq, autoRotate = false, autoRotateSpeed = 1.5, enableRotate = true, frameloop: fl, figDescription, instant = false }) {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768
  // instant=true: Canvas сразу виден (для тёмных карточек где не нужно скрывать инициализацию)
  const [visible, setVisible] = useState(instant)

  // Запускаем preload только когда компонент смонтирован — не блокируем начальную загрузку
  useEffect(() => {
    useGLTF.preload(M_PROJECTOR)
    useGLTF.preload(M_LADDER)
    useGLTF.preload(M_NOZZLE)
    useGLTF.preload(M_DRAIN)
    useGLTF.preload(getSkimmerModelPath('EM0010'))
  }, [])

  const eqList = [
    eq?.light  && 'RGB прожекторы для бассейна',
    eq?.ladder && 'лестница из нержавеющей стали',
    eq?.embed  && 'скиммеры, форсунки, донный слив (закладные элементы)',
  ].filter(Boolean)

  const finishing = finObj?.pictures?.[1]
    ? 'с мозаичной отделкой (Reviglass, стеклянная мозаика)'
    : finObj?.pictures?.[0]
    ? 'с ПВХ плёнкой (CGT, армированная плёнка)'
    : ''

  const caption = figDescription || [
    `Интерактивная 3D визуализация бассейна ${len}×${wid}×${dep} м`,
    finishing,
    eqList.length
      ? `Оборудование: ${eqList.join(', ')}. Марки: Pahlen, Emaux, Landy.`
      : 'Подбор насосов, фильтров и нагрева для бассейна — Pahlen, Emaux, Landy.',
    `Конфигуратор бассейна онлайн Welland Create — расчёт и подбор оборудования для бассейна в Казахстане (Алматы, Астана, Шымкент). Скиммерный и переливной бассейн под ключ.`,
  ].filter(Boolean).join(' ')

  return (
    <figure
      aria-label={`3D модель бассейна ${len}×${wid} м — Welland Create`}
      style={{ width: '100%', height: '100%', margin: 0, padding: 0, position: 'relative', opacity: visible ? 1 : 0, transition: instant ? 'none' : 'opacity 0.6s ease' }}
    >
      <Canvas
        camera={{ fov: CAM_FOV, near: 0.05, far: 200 }}
        frameloop={fl ?? (autoRotate ? 'always' : 'demand')}
        gl={{
          alpha: true,
          antialias: !isMobile,
          powerPreference: 'default',
          preserveDrawingBuffer: false,
          failIfMajorPerformanceCaveat: false,
        }}
        dpr={isMobile ? [1, 1] : [1, 1.25]}
        onCreated={({ gl }) => {
          gl.toneMapping = THREE.ACESFilmicToneMapping
          gl.toneMappingExposure = 1.2
          const canvas = gl.domElement
          canvas.addEventListener('webglcontextlost', (e) => { e.preventDefault() }, false)
          canvas.addEventListener('webglcontextrestored', () => { gl.setSize(canvas.clientWidth, canvas.clientHeight) }, false)
        }}
        style={{ width: '100%', height: '100%', display: 'block' }}
      >
        <SceneContents len={len} wid={wid} dep={dep} finObj={finObj} eq={eq} autoRotate={autoRotate} autoRotateSpeed={autoRotateSpeed} enableRotate={enableRotate} onReady={() => setVisible(true)} />
      </Canvas>
      {/* Текстовое описание для Google (sr-only: видно только поисковикам и скринридерам) */}
      <figcaption style={{
        position: 'absolute', width: 1, height: 1, padding: 0,
        margin: -1, overflow: 'hidden', clip: 'rect(0,0,0,0)',
        whiteSpace: 'nowrap', borderWidth: 0,
      }}>
        {caption}
      </figcaption>
    </figure>
  )
}
